import {
  setLocation,
  setLocations,
  setLocationSearchResults,
  setLocationsAll,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fetch a single location by ID
export const fetchLocation = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/locations/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch location");

    const location = await response.json();
    dispatch(setLocation(location));
    return location;
  } catch (error) {
    console.error("Error while fetching location:", error);
    return { error: error.message };
  }
};

// Fetch all locations with optional filters
export const fetchLocations =
  ({ page = 1, pageSize = 10, sort, search } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        page: Number(page), // Ensure page is correctly formatted
        limit: Number(pageSize), // Use 'limit' instead of 'pageSize'
        sort,
        search,
      }).toString();

      const response = await fetch(`${BASE_URL}/locations?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch locations");

      const data = await response.json();

      dispatch(
        setLocations({
          data: data.locations,
          total: data.total,
        })
      );
      return {
        data: data.locations,
        total: data.total,
      };
    } catch (error) {
      console.error("Error while fetching locations:", error);
      return { error: error.message };
    }
  };

// Fetch all locations
export const fetchAllLocations = () => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/locations/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch locations");

    const locations = await response.json();

    dispatch(setLocationsAll(locations));

    return locations;
  } catch (error) {
    console.error("Error while fetching locations:", error);
    return { error: error.message };
  }
};

// Create a new location
export const createLocation = (values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/locations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });
    if (!response.ok) throw new Error("Failed to create location");

    const location = await response.json();
    dispatch(setLocation(location));
    dispatch(fetchLocations());
    return location;
  } catch (error) {
    console.error("Error while creating location:", error);
    return { error: error.message };
  }
};

// Update a location by ID
export const updateLocation = (id, values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/locations/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });
    if (!response.ok) throw new Error("Failed to update location");

    const location = await response.json();
    dispatch(setLocation(location));
    dispatch(fetchLocations());
    return location;
  } catch (error) {
    console.error("Error while updating location:", error);
    return { error: error.message };
  }
};

// Delete a location by ID
export const deleteLocation = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/locations/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Failed to delete location");

    const { locations } = getState().auth; // Assuming auth slice contains locations
    const updatedLocations = locations.filter(
      (location) => location._id !== id
    );
    dispatch(setLocations(updatedLocations));
    return id;
  } catch (error) {
    console.error("Error while deleting location:", error);
    return { error: error.message };
  }
};

// Search locations by name
export const searchLocations =
  (searchLocationTerm) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/locations/search?search=${searchLocationTerm}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to search locations");

      const locations = await response.json();
      dispatch(setLocationSearchResults(locations));
      return locations;
    } catch (error) {
      console.error("Error while searching locations:", error);
      return { error: error.message };
    }
  };
